import { NgZone } from '@angular/core';
import { ENVIRONMENTS } from '../../../environments/environments';
import { User } from '../../../vas/business-objects/user/user.model';
import { VasAppFacade } from '../../../vas/services/vas-app-facade.service';
import { Session } from '../../business-objects/session/session.model';
import { ISchedulable } from './schedulable.interface';
import { ScheduledTask } from './scheduled-task';
import { ENV } from '../../../environments/environment';

export interface ScheduleItemConfig {
    id: string;
    service: ISchedulable;
    serviceName: string;
    fnName: string;
    args: Array<any>;
    interval: number;
    priority: number;
    deps: string | Array<string>;
}

export type Schedule = Array<ScheduleItemConfig>;

export class SchedulerService {

    logging = false;
    tasks: Array<ScheduledTask> = [];

    protected activeUser: User;
    protected _taskRunning: ScheduledTask;
    protected paused: boolean = false;
    protected requestedTasks: Array<string> = [];
    protected schedulerInterval;
    protected session: Session;
    protected initialised = false;

    constructor(protected appFacade: VasAppFacade, protected ngZone: NgZone) {

    }

    addTask(task: ScheduledTask): void {
        this.tasks.push(task);
    }

    init(tasks: Array<ScheduledTask>): void {

        if (this.initialised) {
            throw new Error('scheduler is already initialised');
        }

        this.appFacade.session.getSession().subscribe(session => this.session = session);

        // for each task ...
        tasks.forEach(task => {
            // handle dependencies
            tasks
                .filter(t => t !== task)
                .forEach(t => {
                    // @todo use the dependencyIds to choose
                    task.addDependency(t);
                });

            // ... add the task to the scheduler
            this.addTask(task);
        });
    }

    startScheduler(): void {

        if (this.schedulerInterval) {
            return;
        }

        if (this.paused) {
            console.warn('starting scheduler when paused. unpausing');
            this.unpause();
            return;
        }

        if (ENV.name === ENVIRONMENTS.E2E) {
            console.warn('scheduler not starting because this is the E2E environment');
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.schedulerInterval = setInterval(() => {
                try {
                    this.run().catch(error => console.error(error));
                } catch (e) {
                    console.error('scheduler error', e);
                    this.stopScheduler();
                }
            }, 1000);
        });
    }

    stopScheduler(): void {

        if (!this.schedulerInterval) {
            return;
        }

        this.logging && console.log('stopping scheduler');

        clearInterval(this.schedulerInterval);
        this.schedulerInterval = undefined;
        this.taskStopped(this._taskRunning);
    }

    async run(): Promise<any> {
        if (this.paused) {
            return Promise.resolve();
        }

        this.stopRunningTask();

        if (this._taskRunning) {
            this.logging && console.log("task '%s' is already running", this._taskRunning.getId());
            return Promise.resolve();
        }

        const task = this.getNextTask();

        if (!task) {
            return Promise.resolve();
        }

        if (task.getId() === 'refreshOnlineStatus') {
            return this.runTask(task);
        }

        this.runTask(task);
    }

    runTask(task): Promise<any> {
        return new Promise((resolve, reject) => {
            this.taskRunning(task);

            try {
                task.execute().then(
                    () => {
                        this.taskStopped(task);
                        resolve();
                    },
                    error => {
                        const in5mins = new Date((new Date()).getTime() + 5 * 60000);
                        task.setLastRun(in5mins);
                        console.error('scheduled task failed to execute', error, task);
                        this.taskStopped(task);
                        resolve();
                    }
                );
            } catch (error) {
                console.error('scheduled task failed to execute', error, task);
                this.taskStopped(task);
                resolve();
            }
        });
    }

    stopRunningTask(): void {
        if (this._taskRunning && this._taskRunning.timeout()) {
            this._taskRunning.stopService();
            this.taskStopped(this._taskRunning);
        }
    }

    requestTask(id: string): void {
        this.requestedTasks.push(id);
    }

    taskRunning(task: ScheduledTask): void {
        this.logging && console.log("starting task '%s'", task.getId());
        this._taskRunning = task;
    }

    taskStopped(task: ScheduledTask): void {
        if (!this._taskRunning || this._taskRunning.getId() !== task.getId()) {
            return;
        }

        this.logging && console.log("task stopped '%s'", this._taskRunning.getId());
        this._taskRunning = undefined;
    }

    getNextTask(): ScheduledTask {
        if (this.requestedTasks.length) {
            const id = this.requestedTasks.shift();
            return this.tasks.find(t => t.getId() === id);
        }

        const tasks = this.tasks
            // filter only tasks that need to run
            .filter(t => t.getTimeToNextRun() <= 0)
            // filter tasks if their requirements pass
            .filter(t => t.checkRequirements(this.session.getNetworkStatus().isOnline(), this.session.getNetworkStatus().isAuthenticated()))
            // sort tasks
            // - tasks that have been waiting longer will run first
            // - tasks that have the same waiting time will be sorted by priority
            .sort((a, b) => {

                const aTime = a.getTimeToNextRun();
                const bTime = b.getTimeToNextRun();

                if (aTime !== bTime) {
                    return aTime - bTime;
                }

                return a.getPriority() - b.getPriority();
            });

        return tasks.length ? tasks[0] : undefined;

    }

    pause(): void {
        this.paused = true;
    }

    unpause(): void {
        this.paused = false;
    }

}



// WEBPACK FOOTER //
// ./src/core/services/scheduler/scheduler.service.ts